import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { trendsUrl } from '../../utils/trends';
import { withReturnTo } from '../common/returnPath';
import {
  briefNotice,
  briefState,
  describeKeyword,
  briefFootnote,
  headlineKeyword,
  lastSeenLabel,
} from './proposalGrouping';
import WritePageAnyway from './WritePageAnyway';
import { tagLabel } from './TagFilter';
import type { TrendStory, TrendProposal, TrendTag } from '../../types';

/**
 * One trending term, and what we found out about it.
 *
 * The card renders from the FEED first and fills in — term, traffic and the
 * headline are known immediately, and the demand numbers and proposals arrive
 * later over the websocket. That order matters: an empty card that appears only
 * once research finishes would make the page look broken for the ten seconds
 * three APIs take, and most of what makes a term judgeable (the headline) is
 * already in hand.
 */

function Chip({
  tone = 'gray',
  children,
}: {
  tone?: 'gray' | 'green' | 'amber' | 'blue';
  children: React.ReactNode;
}) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  };
  return <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${tones[tone]}`}>{children}</span>;
}

function ProposalRow({
  proposal,
  from,
  onCreate,
  onDismiss,
}: {
  proposal: TrendProposal;
  /** Where "back" goes from the page this opens. */
  from: string;
  onCreate: (p: TrendProposal) => void;
  onDismiss: (p: TrendProposal) => void;
}) {
  const built = proposal.managed_page_id != null;
  return (
    <li className="py-2 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{proposal.title}</p>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{proposal.angle}</p>
          {proposal.rationale && (
            <p className="mt-0.5 text-xs italic text-gray-500 dark:text-gray-400">{proposal.rationale}</p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Would target{' '}
            <span className="font-medium text-gray-700 dark:text-gray-200">{proposal.keyword}</span>
            {proposal.monthly_searches != null && <> · {proposal.monthly_searches.toLocaleString()}/mo</>}
            {proposal.competition && <> · {proposal.competition.toLowerCase()} competition</>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {built ? (
            // Never offers Create twice — the FK on the proposal is what makes
            // this reliable rather than a UI-only guard.
            /* A Link, not an anchor: this was a full page reload, which threw
               away the loaded app and every card's state to move one screen.
               And it carries the way back — going to look at a page you made
               from a spike, with no route to the spike, is the dead end the
               build-context links already fixed in the other direction. */
            <Link
              to={withReturnTo(
                `/page-builder/${proposal.managed_page_id}`,
                from,
                'Trends',
              )}
              className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700"
            >
              Open page
            </Link>
          ) : (
            <>
              {/* "Create page", not "Create". The verb alone left the object
                  unstated on the one control that spends a build — and this is
                  a row full of prose, so a bare verb reads as "create... this
                  paragraph?". It also matches the dialog it opens, which now
                  asks where the PAGE should live. */}
              <button
                onClick={() => onCreate(proposal)}
                className="whitespace-nowrap rounded bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
                title={`Create a page targeting "${proposal.keyword}"`}
              >
                Create page
              </button>
              <button
                onClick={() => onDismiss(proposal)}
                className="icon-btn text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                title="Not worth writing - hide this suggestion"
                aria-label="Dismiss this suggested page"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

/** A trend as a topic room's question: what a researcher writing about it
 *  needs to find out. Pure. */
export function topicQuestion(story: Pick<TrendStory, 'term' | 'news'>): string {
  const headline = story.news?.[0]?.title;
  return (
    `What is the story behind "${story.term}": what happened and when, who is in it, ` +
    `what people are saying, and what has nobody written about yet?` +
    (headline ? ` (In the news: ${headline})` : '')
  );
}

/**
 * DIG INTO IT: open a topic room on this trend in Recon, with the question
 * already written, so the room builds the story, the people, what is said and
 * the angles nobody has covered.
 */
function DigInRecon({ story }: { story: TrendStory }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const open = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const room = await api.createBoardroom({
        name: story.term.slice(0, 200),
        description: topicQuestion(story),
        room_kind: 'topic',
      });
      navigate(`/boardroom/${room.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open a room');
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => void open()}
        disabled={busy}
        title="Open a topic room on this trend: the story, who is in it, what people say, and what nobody has written"
        className="whitespace-nowrap rounded border border-indigo-500 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-400 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
        data-testid="dig-in-recon"
      >
        {busy ? 'Opening…' : 'Dig into it in Recon'}
      </button>
      {error && <span className="max-w-[12rem] text-right text-[11px] text-red-600 dark:text-red-400">{error}</span>}
    </>
  );
}

export default function StoryCard({
  story,
  enriching,
  knownTags = [],
  justResearched,
  onSeen,
  hoistedReason,
  onResearch,
  onCreate,
  onDismiss,
}: {
  story: TrendStory;
  enriching: boolean;
  /** The active vocabulary, so a tag renders as its label not its token. */
  knownTags?: TrendTag[];
  /** Research finished for this term while the reader was on this screen. */
  justResearched?: boolean;
  /** Called when they act on it, which is the signal they have found it. */
  onSeen?: () => void;
  /** A skip reason the SECTION is already showing, so this card stays quiet
   *  about it rather than repeating one account-level setting per row. */
  hoistedReason?: string | null;
  onResearch: (story: TrendStory) => void;
  onCreate: (p: TrendProposal) => void;
  onDismiss: (p: TrendProposal) => void;
}) {
  const [showAllNews, setShowAllNews] = useState(false);
  const location = useLocation();
  // The current Trends URL including its tab and window, so "back" returns to
  // the list as it was rather than to a default view of it.
  const from = `${location.pathname}${location.search}`;
  const brief = story.brief;
  const state = briefState(brief, enriching);
  const rawNotice = briefNotice(brief, state);
  const footnote = briefFootnote(brief, state);
  // Drop it only when it IS the hoisted sentence — a card with a different
  // reason still has something of its own to say.
  const notice =
    hoistedReason && rawNotice && rawNotice.includes(hoistedReason) ? null : rawNotice;
  const lead = headlineKeyword(brief);
  const days = Math.round(story.trending_for_hours / 24);

  const news = story.news;
  const visibleNews = showAllNews ? news : news.slice(0, 1);

  return (
    <div
      id={`trend-card-${story.trending_search_id}`}
      // Clicking anywhere on a highlighted card means it has been found, so the
      // mark goes. Nothing is captured or prevented — this only clears a hint.
      onClickCapture={justResearched ? onSeen : undefined}
      className={`rounded-lg border bg-white p-3 transition-shadow dark:bg-gray-800 ${
        justResearched
          ? // The card you were waiting on. A ring rather than a colour swap so
            // the row still reads normally, and it survives until acted on.
            'border-blue-400 ring-2 ring-blue-300/60 dark:border-blue-500 dark:ring-blue-500/40'
          : story.is_current
          ? 'border-gray-200 dark:border-gray-700'
          : // Dropped out of the feed. Dimmed and dashed rather than hidden:
            // the spike is over but the research is not, and a term you turned
            // into three proposals is still the most useful row on the page.
            'border-dashed border-gray-300 opacity-75 hover:opacity-100 dark:border-gray-600'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className="w-7 shrink-0 pt-0.5 text-right text-xs tabular-nums text-gray-400 dark:text-gray-500"
          title={
            story.is_current
              ? `Google ranks this #${story.rank} of what the country is searching right now`
              : `Was #${story.rank} when it last appeared in the feed`
          }
        >
          {/* A bare grey number beside a headline reads as an index, a count, or
              nothing at all — it got asked about. The "#" is what makes it a
              RANK at a glance, without a column header this list does not have.

              A dropped term keeps its dot: its rank is a frozen reading from
              whenever it was last polled, and printing it would assert a
              position in a feed the term is no longer in. */}
          {story.is_current ? `#${story.rank}` : '·'}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{story.term}</h3>
            {story.approx_traffic && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{story.approx_traffic} searches</span>
            )}
            {story.is_current ? (
              days >= 1 && <Chip>{days}d running</Chip>
            ) : (
              <Chip tone="amber">{lastSeenLabel(story)}</Chip>
            )}
            {justResearched && (
              <Chip tone="blue">just researched</Chip>
            )}
            {story.keyword_id && <Chip tone="green">tracked</Chip>}
            {/* What the filter is matching on. Shown on the card so a chip's
                effect is legible per row rather than only in aggregate. */}
            {(brief?.tags ?? []).map(t => (
              <Chip key={t}>{tagLabel(t, knownTags)}</Chip>
            ))}
            {lead && (
              <span className="text-xs text-gray-500 dark:text-gray-400" title="Google Ads demand for the strongest related term">
                · {describeKeyword(lead)}
              </span>
            )}
          </div>

          {/* The headline is what makes a bare term judgeable at all. */}
          {visibleNews.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {visibleNews.map(n => (
                <div key={n.url}>
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-600 hover:text-blue-600 hover:underline dark:text-gray-300 dark:hover:text-blue-400"
                  >
                    {n.title}
                  </a>
                  {n.source && <span className="text-xs text-gray-400 dark:text-gray-500"> - {n.source}</span>}
                </div>
              ))}
              {news.length > 1 && (
                <button
                  onClick={() => setShowAllNews(v => !v)}
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  {showAllNews ? 'fewer' : `+${news.length - 1} more`}
                </button>
              )}
            </div>
          )}

          {notice && (
            <p
              className={`mt-1.5 text-xs ${
                state === 'failed' || state === 'partial'
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {notice}
            </p>
          )}

          {/* The smaller caveat, UNDER the verdict rather than instead of it.
              This is the line that used to crowd out "nothing worth a page" and
              leave a reader guessing why there was no Create button. */}
          {footnote && (
            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{footnote}</p>
          )}

          {/* The override, on the two states that produced nothing to create.
              Quiet on purpose: findable by someone who has decided, invisible to
              someone skimming for what the page recommends. Not offered while
              research is running or failed — there is nothing to write FROM
              yet, and "anyway" would be answering a question not yet asked. */}
          {(state === 'ready-empty' || state === 'partial') && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <WritePageAnyway story={story} />
            </div>
          )}

          {brief && brief.proposals.length > 0 && (
            <>
              {/* NAME THE THING. Without this the rows are a title, a blurb and
                  a blue button, and the only way to learn that Create makes a
                  PAGE is to press it and watch where you land. The count is
                  here too so the list reads as a shortlist rather than as more
                  of the card's own text. */}
              <p className="mt-2 border-t border-gray-100 pt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:border-gray-700 dark:text-gray-500">
                {brief.proposals.length} suggested page
                {brief.proposals.length === 1 ? '' : 's'}
              </p>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {brief.proposals.map(p => (
                  <ProposalRow
                    key={p.id}
                    proposal={p}
                    from={from}
                    onCreate={onCreate}
                    onDismiss={onDismiss}
                  />
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {state === 'working' ? (
            <span className="text-xs text-gray-400 dark:text-gray-500">researching…</span>
          ) : state === 'unresearched' ? (
            <button
              onClick={() => onResearch(story)}
              className="whitespace-nowrap rounded border border-blue-600 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/30"
            >
              Research this
            </button>
          ) : (
            <button
              onClick={() => onResearch(story)}
              className="whitespace-nowrap text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Research this term again"
            >
              Re-research
            </button>
          )}
          <DigInRecon story={story} />
          <a
            href={trendsUrl(story.term, story.geo)}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap text-xs text-gray-400 hover:underline dark:text-gray-500"
          >
            Trends ↗
          </a>
        </div>
      </div>
    </div>
  );
}
